import { FC, useEffect, useState } from 'react';
import { Button, Icon } from 'common-components';
import classNames from 'classnames';
import styles from './filtersWrapper.module.scss';
import { FilterTypes, FiltersWrapperProps } from './filtersWrapperTypes';
import Accordion from '../../accordion/accordion';
import Select from '../select/select';
import { useTranslationContext } from '../../contexts/translationContext';
import MonthPicker from '../monthPicker/monthPicker';
import MultiSelectSearch from '../multiSelectSearch/multiSelectSearch';
import UserSearch from '../userSearch/userSearch';
import MultiTagsSelect from '../entityAndActionsFilter/multiTagsSelect';
import SelectTimeFrame from '../selectTimeFrame/selectTimeFrame';

const FiltersWrapper: FC<FiltersWrapperProps> = (props: FiltersWrapperProps) => {
    const {
        filters,
        onApplyCallback,
        onCancelCallback,
        width,
        isAbsolute = true,
        initialState = {},
        filtersWrapperCX = '',
        actionsWrapperCX = '',
        onFilterChangeCallback,
        initExpandedFilters,
        filtersWrapperStyle = {}
    } = props;
    const { translate, locale } = useTranslationContext();
    const [filtersState, setFiltersState] = useState<object>({});

    useEffect(() => {
        setFiltersState(initialState);
    }, [initialState]);

    const onFilterChange = (filterKey, data, _event?: any) => {
        const filtersStateCpy = { ...filtersState };

        if (Array.isArray(data) && !data.length) {
            delete filtersStateCpy[filterKey];
        } else {
            filtersStateCpy[filterKey] = data;
        }

        if (Object.keys(data).length > 0) {
            Object.keys(data).forEach(key => {
                if ((Array.isArray(data[key]) && !data[key].length) || !data[key]) {
                    delete filtersStateCpy[filterKey][key];
                }
            });
            if (Object.keys(filtersStateCpy[filterKey]).length === 0) {
                delete filtersStateCpy[filterKey];
            }
        }

        setFiltersState(filtersStateCpy);
        onFilterChangeCallback?.(filtersStateCpy);
    };

    const onApply = () => {
        onApplyCallback(filtersState);
    };

    const onCancel = () => {
        onCancelCallback();
    };

    const getDateString = (date: Date, showDay?: boolean) => {
        return date.toLocaleDateString(String(locale), {
            year: 'numeric',
            month: 'short',
            ...(showDay && { day: 'numeric' })
        });
    };

    const filterInfo = filterKey => {
        if (Array.isArray(filtersState[filterKey]) && filtersState[filterKey].length) {
            return `(${filtersState[filterKey].length})`;
        }
        const filter = filters.find(f => f.key === filterKey);
        if (
            filter?.type === FilterTypes.monthPicker &&
            filtersState[filterKey]?.startTimeMS &&
            filtersState[filterKey]?.endTimeMS
        ) {
            const startTime = new Date(filtersState[filterKey].startTimeMS);
            const endTime = new Date(filtersState[filterKey].endTimeMS);
            return `${getDateString(startTime)} - ${getDateString(endTime)}`;
        }
        if (filter?.type === FilterTypes.multiTagsSelect) {
            const entity = filtersState[filterKey]?.entity && !filtersState[filterKey]?.actions?.length ? 1 : 0;
            const actionsNumber =
                filtersState[filterKey]?.actions?.length > 0 ? filtersState[filterKey]?.actions?.length : 0;
            const entityAndActionsNumber = entity + actionsNumber;
            if (entityAndActionsNumber > 0) return `(${entity + actionsNumber})`;
        }
        if (filter?.type === FilterTypes.timeFrame && filtersState[filterKey]?.to && filtersState[filterKey]?.from) {
            return `${getDateString(filtersState[filterKey]?.from, true)} - ${getDateString(
                filtersState[filterKey]?.to,
                true
            )}`;
        }
        return '';
    };

    const getSelectedOptions = filterKey => {
        return filtersState[filterKey] || [];
    };

    const onClear = (event, filterKey) => {
        event.stopPropagation();
        const filtersStateCpy = { ...filtersState };
        delete filtersStateCpy[filterKey];
        setFiltersState(filtersStateCpy);
        onFilterChangeCallback?.(filtersStateCpy);
    };

    const getTrashIcon = filterKey => {
        if (!filtersState[filterKey]) return null;

        return (
            <span onClick={(event: any) => onClear(event, filterKey)}>
                <Icon name={'trashAdmin'} className={styles['trash-icon']} />
            </span>
        );
    };

    const renderFilter = (filter, index) => {
        let component;
        switch (filter.type) {
            case FilterTypes.select:
                component = (
                    <Select
                        tags={filter.selectFilter.tags}
                        width={filter.selectFilter.width}
                        onTagClickCallback={data => onFilterChange(filter.key, data)}
                        selectedTagsCallback={getSelectedOptions(filter.key)}
                        categories={filter.selectFilter.categories}
                        maxRows={filter.selectFilter.maxRows}
                        maxHeight={filter.selectFilter.maxHeight}
                    />
                );
                break;
            case FilterTypes.monthPicker:
                component = (
                    <MonthPicker
                        yearsAmount={filter.monthPickerFilter.yearsAmount}
                        onRangePicked={(startTime, endTime) =>
                            onFilterChange(filter.key, { startTimeMS: startTime, endTimeMS: endTime })
                        }
                        initialStartTimes={getSelectedOptions(filter.key)}
                    />
                );
                break;
            case FilterTypes.multiSelect:
                component = (
                    <MultiSelectSearch
                        options={filter.multiSelectFilter.options}
                        selectedOptions={getSelectedOptions(filter.key)}
                        scrollerHeight={filter.multiSelectFilter.scrollerHeight}
                        // eslint-disable-next-line
                        onChange={(e, data) => onFilterChange(filter.key, data, e)}
                    />
                );
                break;
            case FilterTypes.userSearch:
                component = (
                    <UserSearch
                        users={filter.userSearchFilter.users}
                        selectedUsers={getSelectedOptions(filter.key)}
                        onChange={(users: string[]) => {
                            onFilterChange(filter.key, users);
                        }}
                        onSearchChange={filter.userSearchFilter.onSearchChange}
                        fetchNextPage={filter.userSearchFilter.fetchNextPage}
                        isFetching={filter.userSearchFilter.isFetching}
                        totalFetched={filter.userSearchFilter.totalFetched}
                        totalRows={filter.userSearchFilter.totalRows}
                        usersFullName={filter.userSearchFilter.usersFullName}
                        listAbsolute={false}
                    />
                );
                break;
            case FilterTypes.multiTagsSelect:
                component = (
                    <MultiTagsSelect
                        tags1={filter.multiTagsSelectFilter.tags1}
                        selectedTags={{
                            tag1: getSelectedOptions(filter.key)?.entity || '',
                            tags2: getSelectedOptions(filter.key)?.actions || []
                        }}
                        onSelect={(tag1: string, tags2: string[]) =>
                            onFilterChange(filter.key, { entity: tag1, actions: tags2 })
                        }
                    />
                );
                break;
            case FilterTypes.timeFrame:
                component = (
                    <SelectTimeFrame
                        limitUntil={filter.timeFrameFilter.limitUntil}
                        onSelectTimeFrame={(timeFrame?: string, from?: Date, to?: Date) =>
                            onFilterChange(filter.key, { timeFrame, from, to })
                        }
                        selectedTimeFrame={getSelectedOptions(filter.key)}
                    />
                );
                break;
        }

        return (
            <Accordion
                title={filter.title}
                key={index}
                width={width}
                info={filterInfo(filter.key)}
                adornment={getTrashIcon(filter.key)}
                initIsExpanded={initExpandedFilters && filter.isExpanded}
            >
                {component}
            </Accordion>
        );
    };

    return (
        <div
            data-testid="filters-wrapper"
            className={classNames(styles['filters-wrapper'], filtersWrapperCX)}
            style={{ position: isAbsolute ? 'absolute' : 'relative', ...filtersWrapperStyle }}
        >
            <div className={styles['filters-container']}>
                {filters.map((filter, index) => {
                    return renderFilter(filter, index);
                })}
            </div>
            <div className={classNames(styles['actions-wrapper'], actionsWrapperCX)}>
                <Button
                    type="learningPanelAdminCancel"
                    text={translate('Cancel')}
                    onClick={onCancel}
                    lowerCase={true}
                />
                <Button
                    type="learningPanelAdminActionButton"
                    text={translate('Apply')}
                    onClick={onApply}
                    lowerCase={true}
                />
            </div>
        </div>
    );
};

export default FiltersWrapper;
